import React, { useState, useEffect, useMemo } from 'react';
import {
  Shield, ShieldCheck, ShieldAlert, Users, UserCheck, UserMinus, UserPlus,
  Lock, Unlock, KeyRound, Search, Filter, RefreshCw, Plus, Edit2, Trash2,
  Mail, Building, User, Phone, Calendar, Clock, Activity, History,
  AlertTriangle, CheckCircle, XCircle, Eye, ChevronDown, MoreVertical,
  Ban, PlayCircle, X, Download, Hash, Send, FileText, Link2, Copy, Loader2,
  Plane, Sprout, List, LayoutGrid
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { StaffHRProfile } from '../../components/hr/StaffHRProfile';
import { ActionLogDrawer } from '../../components/people/ActionLogDrawer';
import { CelebrationsPulse } from '../../components/people/CelebrationsPulse';
import { PeopleKpiStrip } from '../../components/people/PeopleKpiStrip';
import { FilterChips } from '../../components/people/FilterChips';
import { StaffTable } from '../../components/people/StaffTable';
import { StaffCard } from '../../components/people/StaffCard';
import { StaffAvatar } from '../../components/people/StaffAvatar';
import { StaffBulkBar } from '../../components/people/StaffBulkBar';
import { StaffMessageModal } from '../../components/people/StaffMessageModal';
import { StaffQuickViewDrawer } from '../../components/people/StaffQuickViewDrawer';
import { roleMeta, isProbation, isNewThisMonth, downloadCsv, formatDate, relativeTime } from '../../components/people/peopleUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'staff', label: 'Staff', color: 'slate' },
  { value: 'manager', label: 'Manager', color: 'emerald' },
  { value: 'executive', label: 'Executive', color: 'violet' },
  { value: 'admin', label: 'Admin', color: 'cyan' },
  { value: 'superadmin', label: 'Super Admin', color: 'amber' }
];

const DEPARTMENTS = [
  'Operations', 'Customer Service', 'Management', 'Marketing',
  'Training', 'IT', 'HR', 'Finance', 'Security'
];

const StaffManagementPageRevamped = ({ getAuthHeaders, currentUser }) => {
  const [staffAccounts, setStaffAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [kpiFilter, setKpiFilter] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [onLeaveMap, setOnLeaveMap] = useState({});
  const [drawerUsername, setDrawerUsername] = useState(null);
  const [messageRecipients, setMessageRecipients] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [activeTab, setActiveTab] = useState('directory');

  // Modals
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showSuspendModal, setShowSuspendModal] = useState(null);

  // Form state
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [suspendReason, setSuspendReason] = useState('');

  const [newStaffForm, setNewStaffForm] = useState({ username: '', password: '', display_name: '', role: 'staff', email: '', department: '', job_title: '', date_of_birth: '', hire_date: '' });
  const [editFormData, setEditFormData] = useState({ display_name: '', job_title: '', department: '', phone: '', email: '', role: 'staff', date_of_birth: '', hire_date: '' });

  // Resignations
  const [resignations, setResignations] = useState([]);
  const [lockedAccounts, setLockedAccounts] = useState([]);

  // New: HR profile drawer + invite link + bulk ops
  const [profileUsername, setProfileUsername] = useState(null);
  const [actionLogUsername, setActionLogUsername] = useState(null); // Admin action log drawer
  const [onlineUsers, setOnlineUsers] = useState(new Set()); // usernames active in last 5 min
  const [showInviteLink, setShowInviteLink] = useState(null); // {username, link, expires_at}
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [selectedStaff2, setSelectedStaff2] = useState(new Set()); // Bulk selection
  const [showBulkRoleModal, setShowBulkRoleModal] = useState(false);
  const [bulkNewRole, setBulkNewRole] = useState('staff');

  const AVAILABLE_TAGS = ['Staff', 'Executive', 'VIP', 'Moderator', 'Community Champion', 'Verified', 'OG Member', 'Top Contributor'];

  const isAdmin = ['admin', 'executive', 'superadmin'].includes(currentUser?.role);
  const isExecutive = ['executive', 'superadmin'].includes(currentUser?.role);
  const isSuperAdmin = currentUser?.role === 'superadmin';
  const canManageTags = ['superadmin', 'executive'].includes(currentUser?.role);

  const handleUpdateTags = async (username, tags) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.put(`${BACKEND_URL}/api/admin/users/${username}/tags`, { tags }, { headers });
      toast.success('Tags updated');
      setStaffAccounts(prev => prev.map(s => s.username === username ? { ...s, tags } : s));
    } catch { toast.error('Failed to update tags'); }
  };

  const addTag = (staff, tag) => {
    const current = staff.tags || [];
    if (!current.includes(tag)) handleUpdateTags(staff.username, [...current, tag]);
  };

  const removeTag = (staff, tag) => handleUpdateTags(staff.username, (staff.tags || []).filter(t => t !== tag));

  useEffect(() => { fetchAll(); }, []);

  // Poll online presence every 30s so the green dot stays accurate
  useEffect(() => {
    const loadPresence = async () => {
      const h = getAuthHeaders(); if (!h) return;
      try {
        const r = await axios.get(`${BACKEND_URL}/api/admin/people/online-presence`, { headers: h });
        setOnlineUsers(new Set((r.data.online || []).map(o => o.username)));
      } catch {}
    };
    loadPresence();
    const t = setInterval(loadPresence, 30000);
    return () => clearInterval(t);
  }, []);

  const fetchAll = () => {
    fetchStaffAccounts();
    fetchResignations();
    fetchLockedAccounts();
    fetchOnLeave();
  };

  const fetchOnLeave = async () => {
    const h = getAuthHeaders(); if (!h) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/people/on-leave`, { headers: h });
      setOnLeaveMap(Object.fromEntries((r.data.on_leave || []).map(l => [l.username, l])));
    } catch {}
  };

  const fetchStaffAccounts = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/admin/staff-accounts`, { headers });
      setStaffAccounts(res.data || []);
    } catch { toast.error('Failed to load staff'); } finally { setLoading(false); }
  };

  const fetchResignations = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/resignations`, { headers });
      setResignations(res.data || []);
    } catch {}
  };

  const fetchLockedAccounts = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/locked-accounts`, { headers });
      setLockedAccounts(res.data || []);
    } catch {}
  };

  const handleCreateStaff = async () => {
    if (!newStaffForm.username || !newStaffForm.password) {
      toast.error('Username and password required');
      return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/create-staff`, newStaffForm, { headers });
      toast.success('Staff account created');
      setShowStaffForm(false);
      setNewStaffForm({ username: '', password: '', display_name: '', role: 'staff', email: '', department: '', job_title: '' });
      fetchStaffAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
  };

  const handleEditStaff = async () => {
    if (!selectedStaff) return;
    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/admin/staff-accounts/${selectedStaff.username}`, editFormData, { headers });
      toast.success('Staff updated');
      setShowEditModal(false);
      fetchStaffAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Min 6 characters'); return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/admin/staff-accounts/${resetPasswordTarget.username}/reset-password`, { new_password: newPassword }, { headers });
      toast.success('Password reset');
      setShowResetPassword(false);
      setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteStaff = async (staffId, staffUsername) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/admin/staff-accounts/${staffId}`, { headers });
      toast.success(`Deleted ${staffUsername}`);
      setShowDeleteConfirm(null);
      fetchStaffAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleSuspend = async (staff) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/suspend-account/${staff.username}`, { reason: suspendReason }, { headers });
      toast.success(`${staff.display_name || staff.username} suspended`);
      setShowSuspendModal(null);
      setSuspendReason('');
      fetchStaffAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to suspend'); }
  };

  const handleUnsuspend = async (staff) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/unsuspend-account/${staff.username}`, {}, { headers });
      toast.success(`${staff.display_name || staff.username} reactivated`);
      fetchStaffAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to unsuspend'); }
  };

  const handleUnlock = async (username) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/unlock-account/${username}`, {}, { headers });
      toast.success(`${username} unlocked`);
      fetchLockedAccounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleResignation = async (id, action) => {
    if (!id) {
      toast.error('Missing resignation ID — please refresh the list and try again');
      return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/resignations/${id}/${action}`, {}, { headers });
      toast.success(`Resignation ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchResignations();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} resignation`);
    }
  };

  // Generate a one-time password-reset invite link for new or locked-out staff
  const generateInviteLink = async (staff) => {
    setGeneratingInvite(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/staff-accounts/${staff.username}/invite-link`, {}, { headers });
      setShowInviteLink({ username: staff.username, display_name: staff.display_name, ...res.data });
      toast.success('Invite link generated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate link'); }
    setGeneratingInvite(false);
  };

  const copyInviteLink = () => {
    if (!showInviteLink?.link) return;
    const full = showInviteLink.link.startsWith('http') ? showInviteLink.link : `${window.location.origin}${showInviteLink.link}`;
    navigator.clipboard.writeText(full).then(() => toast.success('Copied to clipboard'));
  };

  // Bulk suspend / unsuspend / role change
  const toggleStaffSelect = (username) => {
    setSelectedStaff2(prev => {
      const n = new Set(prev);
      n.has(username) ? n.delete(username) : n.add(username);
      return n;
    });
  };

  const runBulkStaffAction = async (action, extra = {}) => {
    if (selectedStaff2.size === 0) return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/staff-accounts/bulk-action`, {
        usernames: Array.from(selectedStaff2),
        action,
        ...extra
      }, { headers });
      toast.success(`${res.data.affected} staff ${action.replace('_', ' ')}d`);
      setSelectedStaff2(new Set());
      setShowBulkRoleModal(false);
      fetchStaffAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk action failed'); }
  };

  const actions = {
    quickView: (s) => setDrawerUsername(s.username),
    hrProfile: (s) => setProfileUsername(s.username),
    actionLog: (s) => setActionLogUsername(s.username),
    message: (s) => setMessageRecipients([s.username]),
    edit: (s) => { setSelectedStaff(s); setEditFormData({ display_name: s.display_name || '', job_title: s.job_title || '', department: s.department || '', phone: s.phone || '', email: s.email || '', role: s.role || 'staff', date_of_birth: (s.date_of_birth || '').slice(0, 10), hire_date: (s.hire_date || '').slice(0, 10) }); setShowEditModal(true); },
    invite: (s) => generateInviteLink(s),
    reset: (s) => { setResetPasswordTarget(s); setNewPassword(''); setShowResetPassword(true); },
    suspend: (s) => { setShowSuspendModal(s); setSuspendReason(''); },
    unsuspend: (s) => handleUnsuspend(s),
    delete: (s) => setShowDeleteConfirm(s),
  };

  const stats = useMemo(() => ({
    total: staffAccounts.length,
    active: staffAccounts.filter(s => !s.is_suspended).length,
    onLeave: staffAccounts.filter(s => onLeaveMap[s.username]).length,
    newMonth: staffAccounts.filter(isNewThisMonth).length,
    probation: staffAccounts.filter(isProbation).length,
    suspended: staffAccounts.filter(s => s.is_suspended).length,
    locked: lockedAccounts.length,
    pendingResignations: resignations.filter(r => r.status === 'pending').length
  }), [staffAccounts, lockedAccounts, resignations, onLeaveMap]);

  const KPI_FILTERS = {
    active: s => !s.is_suspended, on_leave: s => !!onLeaveMap[s.username], new: isNewThisMonth,
    probation: isProbation, suspended: s => s.is_suspended,
  };

  const filteredStaff = useMemo(() => {
    let result = [...staffAccounts];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s => [s.username, s.display_name, s.email, s.job_title].some(v => v?.toLowerCase().includes(q)));
    }
    if (filterRole !== 'all') result = result.filter(s => s.role === filterRole);
    if (filterDepartment !== 'all') result = result.filter(s => (s.department || 'Unassigned') === filterDepartment);
    if (kpiFilter && KPI_FILTERS[kpiFilter]) result = result.filter(KPI_FILTERS[kpiFilter]);
    if (sortBy === 'name') result.sort((a, b) => (a.display_name || a.username).localeCompare(b.display_name || b.username));
    if (sortBy === 'role') result.sort((a, b) => (a.role || '').localeCompare(b.role || ''));
    if (sortBy === 'newest') result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    if (sortBy === 'recent') result.sort((a, b) => new Date(b.last_login || 0) - new Date(a.last_login || 0));
    return result;
  }, [staffAccounts, searchQuery, filterRole, filterDepartment, kpiFilter, sortBy, onLeaveMap]);

  const departmentOptions = useMemo(() => {
    const counts = {};
    staffAccounts.forEach(s => { const d = s.department || 'Unassigned'; counts[d] = (counts[d] || 0) + 1; });
    return [{ value: 'all', label: 'All', count: staffAccounts.length }].concat(Object.keys(counts).sort().map(d => ({ value: d, label: d, count: counts[d] })));
  }, [staffAccounts]);

  const roleOptions = [{ value: 'all', label: 'All', count: staffAccounts.length }].concat(
    ROLE_OPTIONS.map(r => ({ value: r.value, label: r.label, hex: roleMeta(r.value).hex, count: staffAccounts.filter(s => s.role === r.value).length })).filter(r => r.count > 0)
  );

  const kpis = [
    { id: 'active', label: 'Active', value: stats.active, hex: '#10B981', Icon: UserCheck, sub: `of ${stats.total} staff` },
    { id: 'on_leave', label: 'On leave today', value: stats.onLeave, hex: '#38BDF8', Icon: Plane },
    { id: 'new', label: 'New this month', value: stats.newMonth, hex: '#00E5FF', Icon: UserPlus, sub: new Date().toLocaleDateString('en-GB', { month: 'long' }) },
    { id: 'probation', label: 'On probation', value: stats.probation, hex: '#F59E0B', Icon: Sprout, sub: 'First 90 days' },
    { id: 'suspended', label: 'Suspended', value: stats.suspended, hex: '#F43F5E', Icon: Ban },
    { id: 'locked', label: 'Locked out', value: stats.locked, hex: '#A78BFA', Icon: Lock, sub: 'Opens Locked tab' },
  ];

  const onKpiSelect = (id) => {
    if (id === 'locked') { setActiveTab('locked'); setKpiFilter(null); return; }
    setActiveTab('directory'); setKpiFilter(id);
  };

  const exportStaff = (rows) => {
    if (!rows.length) return toast.error('Nothing to export');
    downloadCsv(rows, ['username', 'display_name', 'email', 'role', 'department', 'job_title', 'hire_date', 'is_suspended', 'last_login'], `staff-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${rows.length} staff`);
  };

  const toggleAllVisible = () => {
    const all = filteredStaff.every(s => selectedStaff2.has(s.username));
    setSelectedStaff2(all ? new Set() : new Set(filteredStaff.map(s => s.username)));
  };

  const drawerStaff = staffAccounts.find(s => s.username === drawerUsername);

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="access-denied">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-white/20" />
          <p className="text-white/50 text-lg">Access restricted to administrators</p>
        </div>
      </div>
    );
  }

  const SUB_TABS = [
    { id: 'directory', label: 'All staff', icon: Users, count: null },
    { id: 'suspended', label: 'Suspended', icon: Ban, count: stats.suspended },
    { id: 'resignations', label: 'Resignations', icon: UserMinus, count: stats.pendingResignations },
    { id: 'locked', label: 'Locked', icon: Lock, count: stats.locked },
  ];

  const emptyState = (Icon, title, sub) => (
    <div className="text-center py-16 rounded-2xl border border-white/[0.06] bg-white/[0.015]">
      <Icon className="w-10 h-10 text-emerald-300/50 mx-auto mb-3" />
      <p className="text-white/60 font-medium">{title}</p>
      {sub && <p className="text-white/35 text-sm mt-1">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="staff-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/70 font-medium mb-1">People · Workforce</p>
          <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight" data-testid="page-title">
            Staff <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">Management</span>
          </h2>
          <p className="text-white/45 text-sm mt-1">Roles, access, status and bulk actions for every team member.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchAll} data-testid="refresh-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs text-white/60 border border-white/[0.08] hover:text-white hover:border-white/20 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button onClick={() => exportStaff(filteredStaff)} data-testid="export-staff-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs text-white/60 border border-white/[0.08] hover:text-white hover:border-white/20 transition-colors">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
          <button onClick={() => setShowStaffForm(true)} data-testid="add-staff-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-[#0a0a14] bg-gradient-to-r from-cyan-400 to-violet-400 shadow-[0_10px_30px_-10px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 transition-transform">
            <Plus className="w-3.5 h-3.5" /> Add staff
          </button>
        </div>
      </div>

      <PeopleKpiStrip items={kpis} active={kpiFilter} onSelect={onKpiSelect} testIdPrefix="staff-kpi" />

      <CelebrationsPulse onOpenStaff={(u) => setProfileUsername(u)} />

      {/* Sub tabs */}
      <div className="flex gap-1 p-1 rounded-2xl bg-white/[0.02] border border-white/[0.06] w-fit overflow-x-auto no-scrollbar">
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-colors ${activeTab === t.id ? 'bg-gradient-to-r from-cyan-500/20 to-violet-500/15 text-white border border-cyan-400/30' : 'text-white/50 hover:text-white border border-transparent'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
            {t.count > 0 && <span className="px-1.5 rounded-full bg-rose-500/20 text-rose-300 text-[9px] font-bold">{t.count}</span>}
          </button>
        ))}
      </div>

      {activeTab === 'directory' && (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4 space-y-3">
            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search name, username, email or job title…" data-testid="search-staff"
                  className="w-full pl-10 pr-4 py-2.5 bg-black/40 border border-white/[0.07] rounded-xl text-white placeholder-white/25 focus:border-cyan-400/40 focus:outline-none text-sm" />
              </div>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="sort-staff"
                className="px-3 py-2.5 bg-black/40 border border-white/[0.07] rounded-xl text-sm text-white/70 focus:outline-none">
                <option value="name">Name A–Z</option>
                <option value="role">By role</option>
                <option value="newest">Newest first</option>
                <option value="recent">Recently active</option>
              </select>
              <div className="flex rounded-xl border border-white/[0.07] bg-black/40 p-0.5">
                {[['table', List], ['cards', LayoutGrid]].map(([m, Ic]) => (
                  <button key={m} onClick={() => setViewMode(m)} data-testid={`view-mode-${m}`}
                    className={`px-3 py-1.5 rounded-lg transition-colors ${viewMode === m ? 'bg-cyan-500/20 text-cyan-200' : 'text-white/40 hover:text-white/70'}`}><Ic className="w-4 h-4" /></button>
                ))}
              </div>
            </div>
            <FilterChips label="Department" options={departmentOptions} value={filterDepartment} onChange={setFilterDepartment} testIdPrefix="dept-chip" />
            <FilterChips label="Role" options={roleOptions} value={filterRole} onChange={setFilterRole} testIdPrefix="role-chip" />
            {(kpiFilter || filterRole !== 'all' || filterDepartment !== 'all' || searchQuery) && (
              <button onClick={() => { setKpiFilter(null); setFilterRole('all'); setFilterDepartment('all'); setSearchQuery(''); }} data-testid="clear-staff-filters"
                className="text-[11px] text-cyan-300/80 hover:text-cyan-200 inline-flex items-center gap-1"><X className="w-3 h-3" /> Clear all filters</button>
            )}
          </div>

          {selectedStaff2.size > 0 && (
            <StaffBulkBar count={selectedStaff2.size} canChangeRole={isSuperAdmin}
              onMessage={() => setMessageRecipients(Array.from(selectedStaff2))}
              onExport={() => exportStaff(staffAccounts.filter(s => selectedStaff2.has(s.username)))}
              onRole={() => setShowBulkRoleModal(true)}
              onSuspend={() => runBulkStaffAction('suspend', { reason: 'Bulk suspension' })}
              onUnsuspend={() => runBulkStaffAction('unsuspend')}
              onClear={() => setSelectedStaff2(new Set())} />
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-cyan-400 animate-spin" /></div>
          ) : viewMode === 'table' ? (
            <StaffTable rows={filteredStaff} total={staffAccounts.length} selected={selectedStaff2} onToggle={toggleStaffSelect} onToggleAll={toggleAllVisible}
              online={onlineUsers} onLeaveMap={onLeaveMap} isProbation={isProbation} currentUsername={currentUser?.username} actions={actions} />
          ) : filteredStaff.length === 0 ? emptyState(Users, 'No staff match these filters') : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="staff-card-grid">
              {filteredStaff.map((s, i) => (
                <StaffCard key={s.username} index={i} staff={s} selected={selectedStaff2.has(s.username)} onToggle={toggleStaffSelect}
                  online={onlineUsers.has(s.username)} onLeave={!!onLeaveMap[s.username]} probation={isProbation(s)}
                  isSelf={s.username === currentUser?.username} actions={actions} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'suspended' && (
        <div className="space-y-3">
          {staffAccounts.filter(s => s.is_suspended).length === 0 ? emptyState(CheckCircle, 'No suspended accounts', 'All staff accounts are active') :
            staffAccounts.filter(s => s.is_suspended).map(staff => (
              <div key={staff.username} data-testid={`suspended-${staff.username}`}
                className="flex items-center justify-between p-4 rounded-2xl bg-rose-500/[0.04] border border-rose-400/20">
                <div className="flex items-center gap-3">
                  <StaffAvatar staff={staff} />
                  <div>
                    <p className="text-white font-medium text-sm">{staff.display_name || staff.username}</p>
                    <p className="text-rose-300/70 text-xs">
                      Suspended {staff.suspended_at ? relativeTime(staff.suspended_at) : ''} by {staff.suspended_by || 'admin'}
                      {staff.suspend_reason && <span className="text-white/40"> — {staff.suspend_reason}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => handleUnsuspend(staff)} size="sm" data-testid={`reactivate-${staff.username}`}
                    className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-400/25 rounded-full">
                    <PlayCircle className="w-3.5 h-3.5 mr-1.5" /> Reactivate
                  </Button>
                  <Button onClick={() => setShowDeleteConfirm(staff)} size="sm" variant="outline" data-testid={`suspended-delete-${staff.username}`}
                    className="border-rose-400/25 text-rose-300 hover:bg-rose-500/10 bg-transparent rounded-full">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
        </div>
      )}

      {activeTab === 'resignations' && (
        <div className="space-y-3">
          {resignations.length === 0 ? emptyState(CheckCircle, 'No resignation requests') : resignations.map(r => (
            <div key={r.id} data-testid={`resignation-${r.id}`} className={`p-4 rounded-2xl border ${r.status === 'pending' ? 'bg-orange-500/[0.04] border-orange-400/20' : 'bg-white/[0.015] border-white/[0.06]'}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center"><User className="w-5 h-5 text-white/40" /></div>
                  <div>
                    <p className="text-white font-medium text-sm">{r.display_name || r.username}</p>
                    <p className="text-white/40 text-xs">@{r.username}</p>
                  </div>
                </div>
                <Badge className={`text-[10px] border ${r.status === 'pending' ? 'bg-orange-500/10 text-orange-300 border-orange-400/20' : r.status === 'approved' ? 'bg-emerald-500/10 text-emerald-300 border-emerald-400/20' : 'bg-rose-500/10 text-rose-300 border-rose-400/20'}`}>{r.status}</Badge>
              </div>
              {r.reason && <p className="text-sm text-white/60 mt-3 p-3 rounded-xl bg-white/[0.03]">{r.reason}</p>}
              <div className="flex items-center justify-between mt-3">
                <div className="text-xs text-white/35 flex gap-4">
                  <span>{formatDate(r.created_at)}</span>
                  {r.last_day && <span>Last day: {formatDate(r.last_day)}</span>}
                </div>
                {r.status === 'pending' && isExecutive && (
                  <div className="flex gap-2">
                    <Button onClick={() => handleResignation(r.id, 'approve')} size="sm" data-testid={`approve-resignation-${r.id}`} className="bg-emerald-500/20 text-emerald-300 border border-emerald-400/20 rounded-full">Approve</Button>
                    <Button onClick={() => handleResignation(r.id, 'reject')} size="sm" variant="outline" data-testid={`reject-resignation-${r.id}`} className="border-rose-400/20 text-rose-300 bg-transparent rounded-full">Reject</Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'locked' && (
        <div className="space-y-3">
          {lockedAccounts.length === 0 ? emptyState(Unlock, 'No locked accounts', 'All accounts are accessible') : lockedAccounts.map((acc, i) => (
            <div key={i} data-testid={`locked-${acc.username}`} className="flex items-center justify-between p-4 rounded-2xl bg-amber-500/[0.04] border border-amber-400/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center"><Lock className="w-5 h-5 text-amber-300" /></div>
                <div>
                  <p className="text-white font-medium text-sm">{acc.username}</p>
                  <p className="text-amber-300/70 text-xs">{acc.attempts} failed attempts · Unlocks in {Math.ceil(acc.remaining_seconds / 60)}m</p>
                </div>
              </div>
              {isSuperAdmin && (
                <Button onClick={() => handleUnlock(acc.username)} size="sm" data-testid={`unlock-${acc.username}`}
                  className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-400/25 rounded-full">
                  <Unlock className="w-3.5 h-3.5 mr-1.5" /> Unlock
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {drawerStaff && (
        <StaffQuickViewDrawer staff={drawerStaff} onClose={() => setDrawerUsername(null)}
          online={onlineUsers.has(drawerStaff.username)} onLeave={onLeaveMap[drawerStaff.username]} probation={isProbation(drawerStaff)}
          isSelf={drawerStaff.username === currentUser?.username} actions={actions}
          tagProps={canManageTags ? { available: AVAILABLE_TAGS, onAdd: (t) => addTag(drawerStaff, t), onRemove: (t) => removeTag(drawerStaff, t) } : null} />
      )}

      <StaffMessageModal recipients={messageRecipients} onClose={() => setMessageRecipients(null)} onSent={() => setSelectedStaff2(new Set())} getAuthHeaders={getAuthHeaders} />

      {/* Add Staff Modal */}
      <Dialog open={showStaffForm} onOpenChange={setShowStaffForm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="add-staff-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-cyan-400"><UserPlus className="w-5 h-5" /> Add Staff Member</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            {[
              { key: 'username', label: 'Username', placeholder: 'e.g. john_doe', required: true },
              { key: 'password', label: 'Password', placeholder: 'Min 6 characters', required: true, type: 'password' },
              { key: 'display_name', label: 'Display Name', placeholder: 'e.g. John Doe' },
              { key: 'email', label: 'Email', placeholder: 'john@example.com' },
              { key: 'job_title', label: 'Job Title', placeholder: 'e.g. Customer Support' },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wider">{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                <input type={f.type || 'text'} value={newStaffForm[f.key]} onChange={(e) => setNewStaffForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  data-testid={`new-${f.key}`} placeholder={f.placeholder}
                  className="w-full px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-cyan-500/40 focus:outline-none text-sm" />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wider">Role</label>
                <select value={newStaffForm.role} onChange={(e) => setNewStaffForm(prev => ({ ...prev, role: e.target.value }))} data-testid="new-role"
                  className="w-full px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white text-sm focus:outline-none">
                  {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wider">Department</label>
                <select value={newStaffForm.department} onChange={(e) => setNewStaffForm(prev => ({ ...prev, department: e.target.value }))} data-testid="new-department"
                  className="w-full px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white text-sm focus:outline-none">
                  <option value="">Select...</option>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => setShowStaffForm(false)} variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400">Cancel</Button>
              <Button onClick={handleCreateStaff} size="sm" data-testid="confirm-add-staff" className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="edit-staff-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-400"><Edit2 className="w-5 h-5" /> Edit {selectedStaff?.display_name || selectedStaff?.username}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            {[
              { key: 'display_name', label: 'Display Name' },
              { key: 'job_title', label: 'Job Title' },
              { key: 'email', label: 'Email' },
              { key: 'phone', label: 'Phone' },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wider">{f.label}</label>
                <input type="text" value={editFormData[f.key]} onChange={(e) => setEditFormData(prev => ({ ...prev, [f.key]: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-amber-500/40 focus:outline-none text-sm" />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wider">Role</label>
                <select value={editFormData.role} onChange={(e) => setEditFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white text-sm focus:outline-none">
                  {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wider">Department</label>
                <select value={editFormData.department} onChange={(e) => setEditFormData(prev => ({ ...prev, department: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white text-sm focus:outline-none">
                  <option value="">Select...</option>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wider">Date of birth</label>
                <input type="date" value={editFormData.date_of_birth || ''} onChange={(e) => setEditFormData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                  data-testid="edit-dob"
                  className="w-full px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white text-sm focus:border-rose-400/40 focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wider">Hire date</label>
                <input type="date" value={editFormData.hire_date || ''} onChange={(e) => setEditFormData(prev => ({ ...prev, hire_date: e.target.value }))}
                  data-testid="edit-hire-date"
                  className="w-full px-3 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white text-sm focus:border-amber-400/40 focus:outline-none" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => setShowEditModal(false)} variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400">Cancel</Button>
              <Button onClick={handleEditStaff} size="sm" data-testid="confirm-edit-staff" className="flex-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/20">
                <CheckCircle className="w-4 h-4 mr-1.5" /> Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPassword} onOpenChange={() => { setShowResetPassword(false); setNewPassword(''); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="reset-password-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-400"><KeyRound className="w-5 h-5" /> Reset Password</DialogTitle></DialogHeader>
          {resetPasswordTarget && (
            <div className="space-y-4 mt-3">
              <p className="text-sm text-slate-400">Reset password for <span className="text-white font-medium">{resetPasswordTarget.display_name || resetPasswordTarget.username}</span></p>
              <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password-input"
                placeholder="Min 6 characters"
                className="w-full px-4 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-amber-500/40 focus:outline-none text-sm" />
              <div className="flex gap-2">
                <Button onClick={() => setShowResetPassword(false)} variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400">Cancel</Button>
                <Button onClick={handleResetPassword} size="sm" data-testid="confirm-reset-btn" className="flex-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/20">
                  <KeyRound className="w-4 h-4 mr-1.5" /> Reset
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspend Modal */}
      <Dialog open={!!showSuspendModal} onOpenChange={() => setShowSuspendModal(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="suspend-modal">
          {showSuspendModal && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-400"><Ban className="w-5 h-5" /> Suspend Account</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-3">
                <p className="text-sm text-slate-400">
                  Suspend <span className="text-white font-medium">{showSuspendModal.display_name || showSuspendModal.username}</span>? They will be unable to log in until reactivated.
                </p>
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5 uppercase tracking-wider">Reason (optional)</label>
                  <textarea value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} data-testid="suspend-reason"
                    placeholder="e.g. Policy violation, investigation pending..."
                    className="w-full px-4 py-2.5 bg-slate-800/80 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-red-500/40 focus:outline-none text-sm resize-none h-20" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setShowSuspendModal(null)} variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400">Cancel</Button>
                  <Button onClick={() => handleSuspend(showSuspendModal)} size="sm" data-testid="confirm-suspend"
                    className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20">
                    <Ban className="w-4 h-4 mr-1.5" /> Suspend
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="delete-confirm-modal">
          {showDeleteConfirm && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-400"><AlertTriangle className="w-5 h-5" /> Delete Staff Account</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-3">
                <p className="text-sm text-slate-400">
                  Permanently delete <span className="text-white font-medium">{showDeleteConfirm.display_name || showDeleteConfirm.username}</span>? This cannot be undone.
                </p>
                <div className="flex gap-2">
                  <Button onClick={() => setShowDeleteConfirm(null)} variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400">Cancel</Button>
                  <Button onClick={() => handleDeleteStaff(showDeleteConfirm.id, showDeleteConfirm.username)} size="sm" data-testid="confirm-delete"
                    className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* HR Profile drawer */}
      {profileUsername && (
        <StaffHRProfile username={profileUsername} onClose={() => setProfileUsername(null)} />
      )}

      {/* Admin action log drawer */}
      {actionLogUsername && (
        <ActionLogDrawer scope="staff" identifier={actionLogUsername} displayName={actionLogUsername}
          onClose={() => setActionLogUsername(null)} />
      )}

      {/* Invite Link modal */}
      <Dialog open={!!showInviteLink} onOpenChange={() => setShowInviteLink(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="invite-link-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-300">
              <Link2 className="w-5 h-5" /> Password-reset invite link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <p className="text-sm text-slate-400">Share this one-time link with <span className="text-white font-medium">@{showInviteLink?.username}</span> ({showInviteLink?.display_name || ''}). They can set their own password — you never need to see it.</p>
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 flex items-center gap-2">
              <code className="flex-1 text-xs text-cyan-200 break-all font-mono">{showInviteLink?.link}</code>
              <Button onClick={copyInviteLink} size="sm" data-testid="copy-invite-link-btn"
                className="bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 border border-violet-500/30 flex-shrink-0">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Expires {showInviteLink?.expires_at ? new Date(showInviteLink.expires_at).toLocaleString('en-GB') : ''}
            </p>
            <div className="flex justify-end pt-1">
              <Button onClick={() => setShowInviteLink(null)} variant="outline" size="sm" className="border-slate-700 text-slate-400 hover:bg-slate-800">Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk role-change modal */}
      <Dialog open={showBulkRoleModal} onOpenChange={setShowBulkRoleModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm" data-testid="bulk-role-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-300">
              <Shield className="w-5 h-5" /> Change role for {selectedStaff2.size} staff
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <select value={bulkNewRole} onChange={(e) => setBulkNewRole(e.target.value)}
              data-testid="bulk-role-select"
              className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm focus:border-violet-500/50 focus:outline-none">
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <p className="text-[11px] text-amber-300/80">⚠ Admin & Superadmin can only be granted by a Superadmin.</p>
            <div className="flex gap-2 pt-1">
              <Button onClick={() => setShowBulkRoleModal(false)} variant="outline" size="sm" className="flex-1 border-slate-700 text-slate-400 hover:bg-slate-800">Cancel</Button>
              <Button onClick={() => runBulkStaffAction('role_change', { role: bulkNewRole })} size="sm"
                data-testid="bulk-role-confirm-btn"
                className="flex-1 bg-violet-500/20 hover:bg-violet-500/30 text-violet-300 border border-violet-500/30">
                <Shield className="w-4 h-4 mr-1.5" /> Apply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagementPageRevamped;
